import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Picker from 'emoji-picker-react';
import {
  Button, Input, InputGroup, Checkbox, Drawer, IconButton, Whisper, Tooltip, CheckPicker, Loader,
} from 'rsuite';
import StarIcon from '@rsuite/icons/legacy/Star';
import SearchIcon from '@rsuite/icons/Search';
import CloseIcon from '@rsuite/icons/Close';
import { FaCog, FaRegSmile, FaCommentDots, FaInbox } from 'react-icons/fa';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { toastSuccess, toastError } from '../../../toasts';
import { CustomToggle } from '../../../components/CustomToggle';
import { CustomInput } from '../../../components/CustomInput';
import TagRender from '../../../components/TagRender';
import ChatPreview from './ChatPreview';
import ResumeEditor from './ResumeEditor';
import { initialConfigs, initialValues } from './seed';
import {
  RESPONSE_TYPES, getResponseType, isStatusMessage, GENERAL_PLACEHOLDERS,
  RESUME_PREVIEW_SCENARIOS, readResumeSections, composeResumeText, renderResumePreview,
  milissegundosParaFormatoHora, milissegundosParaMinutos, minutosParaMilissegundos, filtrarNumerosDoTexto,
} from './engine';
import { styles } from './styles';

const TEXTAREA_ID = 'generic-msg-textarea';
const showsKeywords = (a) => !isStatusMessage(a) && !a.isResume;

// Tab "Templates do Sistema": mensagens do robô (palavra-chave, primeiro
// contato, status do pedido e o resumo), em mestre-detalhe de 3 colunas —
// lista | prévia no WhatsApp | edição. Salva tudo via chatbot/create|update.
export default function GenericMessages() {
  const { id_empresa } = useAuth();
  const { theme } = useCustomTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existValues, setExistValues] = useState(true);
  const [steps, setSteps] = useState(initialValues);
  const [configs, setConfigs] = useState(initialConfigs);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyFavorite, setOnlyFavorite] = useState(false);
  const [onlyEnabled, setOnlyEnabled] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [scenarioKey, setScenarioKey] = useState(RESUME_PREVIEW_SCENARIOS[0].key);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);
  const pickerBtnRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`chatbot/find/${id_empresa}`);
      if (!data || data.length === 0) {
        setExistValues(false);
      } else {
        setExistValues(true);
        setSteps(data.answer);
        setConfigs(data.chatbot);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [id_empresa]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!showPicker) return undefined;
    const onClickOutside = (e) => {
      if (pickerRef.current?.contains(e.target) || pickerBtnRef.current?.contains(e.target)) return;
      setShowPicker(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showPicker]);

  const selected = useMemo(
    () => (selectedId === null ? null : steps.find((s) => +s.Id === +selectedId) ?? null),
    [selectedId, steps],
  );

  const scenario = RESUME_PREVIEW_SCENARIOS.find((s) => s.key === scenarioKey) || RESUME_PREVIEW_SCENARIOS[0];

  const previewText = useMemo(() => {
    if (!selected) return '';
    if (!selected.isResume) return selected.text || '';
    const composed = composeResumeText(readResumeSections(selected.variations));
    return renderResumePreview(composed, selected.variations, scenario);
  }, [selected, scenario]);

  const typeCounts = useMemo(() => {
    const c = {};
    RESPONSE_TYPES.forEach((t) => { c[t.key] = steps.filter(t.match).length; });
    return c;
  }, [steps]);

  const typePickerData = RESPONSE_TYPES.map((t) => ({ label: `${t.label} (${typeCounts[t.key] ?? 0})`, value: t.key }));

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return steps.filter((r) => {
      if (onlyEnabled && !r.Ativo) return false;
      if (onlyFavorite && !r.isFavorite) return false;
      if (selectedTypes.length > 0) {
        const t = getResponseType(r);
        if (!t || !selectedTypes.includes(t.key)) return false;
      }
      if (q) {
        const hay = `${r.name ?? ''} ${r.text ?? ''} ${r.keywords ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [steps, onlyEnabled, onlyFavorite, selectedTypes, search]);

  const hasActiveFilters = selectedTypes.length > 0 || onlyFavorite || onlyEnabled || !!search;

  // ── Mutations locais ──
  const patchStep = (id, patch) => setSteps((prev) => prev.map((s) => (+s.Id === +id ? { ...s, ...patch } : s)));
  const changeKeywords = (id, arr) => patchStep(id, { keywords: JSON.stringify(arr) });
  const setFirstContact = (id, firstContact) =>
    setSteps((prev) => prev.map((s) => ({ ...s, firstContact: +s.Id === +id ? firstContact : false })));

  const insertInTextarea = (id, token) => {
    const input = document.getElementById(TEXTAREA_ID);
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const before = input.value[start - 1];
    const after = input.value[end];
    const sepBefore = before === '}' ? ' ' : '';
    const sepAfter = after === '{' ? ' ' : '';
    const insert = sepBefore + token + sepAfter;
    const next = input.value.substring(0, start) + insert + input.value.substring(end);
    patchStep(id, { text: next });
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + insert.length;
      input.selectionStart = pos;
      input.selectionEnd = pos;
    });
  };

  const addEmoji = (id, emoji) => {
    if (emoji) patchStep(id, { text: `${selected?.text ?? ''}${emoji}` });
    setShowPicker(false);
  };

  // ── Persistência ──
  const saveAll = async () => {
    setSaving(true);
    setExistValues(true);
    try {
      const payload = { ...configs, companyId: id_empresa, chatbotanswers: steps };
      if (configs?.Id) await api.put(`chatbot/update/${configs.Id}`, payload);
      else await api.post('chatbot/create', payload);
      setShowPicker(false);
      await reload();
      toastSuccess('Alterações salvas');
    } catch (error) {
      console.log(error);
      toastError('Não foi possível salvar as alterações');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, Ativo) => {
    patchStep(id, { Ativo });
    if (!id) return;
    try {
      await api.post(`chatbotanswers/update/${id}`, { Ativo });
    } catch (error) {
      console.error(error);
      patchStep(id, { Ativo: !Ativo });
      toastError('Não foi possível atualizar o status da mensagem');
    }
  };

  const changeConfig = (key, value) => setConfigs((prev) => ({ ...prev, [key]: value }));
  const changeResetMessage = (time) => changeConfig('intervalMessage', minutosParaMilissegundos(filtrarNumerosDoTexto(time)));

  const selType = selected ? getResponseType(selected) : null;

  if (loading) {
    return <Loader center size="md" content="Carregando mensagens..." style={{ padding: 60 }} />;
  }

  if (!existValues) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon(theme)}><FaCommentDots size={26} /></div>
        <p style={styles.emptyText(theme)}>Você ainda não criou um fluxo de respostas para o seu robô. Deseja criar o fluxo padrão agora?</p>
        <Button appearance="primary" loading={saving} onClick={saveAll} style={{ backgroundColor: theme.colors.primary }}>Criar fluxo padrão</Button>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.sysBar}>
        <CheckPicker data={typePickerData} value={selectedTypes} onChange={setSelectedTypes} placeholder="Tipo de resposta" cleanable searchable={false} size="sm" style={{ minWidth: 190 }} />
        <Checkbox checked={onlyFavorite} onChange={() => setOnlyFavorite(!onlyFavorite)}>Favoritas</Checkbox>
        <Checkbox checked={onlyEnabled} onChange={() => setOnlyEnabled(!onlyEnabled)}>Ativas</Checkbox>
        {hasActiveFilters && (
          <Button size="sm" appearance="link" onClick={() => { setSelectedTypes([]); setOnlyFavorite(false); setOnlyEnabled(false); setSearch(''); }}>Limpar</Button>
        )}
        <span style={{ marginLeft: 'auto' }} />
        <Button appearance="ghost" startIcon={<FaCog />} onClick={() => setSettingsOpen(true)}>Configurações do robô</Button>
      </div>

      <div style={styles.panes}>
        {/* Coluna 1 — lista */}
        <div style={styles.listPane(theme)}>
          <div style={styles.listHead(theme)}>
            <InputGroup inside size="sm">
              <Input placeholder="Buscar mensagem..." value={search} onChange={setSearch} />
              <InputGroup.Addon>{search ? <CloseIcon style={{ cursor: 'pointer' }} onClick={() => setSearch('')} /> : <SearchIcon />}</InputGroup.Addon>
            </InputGroup>
          </div>
          <div style={styles.listScroll}>
            {visible.length === 0 ? (
              <div style={styles.listEmpty(theme)}>Nenhuma mensagem para este filtro.</div>
            ) : (
              visible.map((r) => {
                const type = getResponseType(r);
                const accent = type?.hex ?? '#94a3b8';
                const sel = +selectedId === +r.Id;
                return (
                  <div key={r.Id} role="button" tabIndex={0} onClick={() => setSelectedId(String(r.Id))}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(String(r.Id)); } }}
                    style={styles.msgRow(theme, sel)}>
                    <span style={styles.rowDot(accent)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.rowName(theme, !r.Ativo)}>{r.name}</div>
                      {type && <div style={styles.rowType(theme)}>{type.label}</div>}
                    </div>
                    <button type="button" style={styles.starBtn(!!r.isFavorite)} onClick={(e) => { e.stopPropagation(); patchStep(r.Id, { isFavorite: !r.isFavorite }); }} title="Favoritar"><StarIcon /></button>
                    <span onClick={(e) => e.stopPropagation()}>
                      <CustomToggle size="sm" checked={!!r.Ativo} onChange={() => toggleActive(r.Id, !r.Ativo)} />
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Coluna 2 — prévia */}
        <div style={styles.previewPane(theme)}>
          {selected ? (
            <>
              <div style={styles.previewHead}>
                <span style={styles.previewLabel(theme)}>Prévia no WhatsApp</span>
                {selected.isResume && (
                  <select value={scenarioKey} onChange={(e) => setScenarioKey(e.target.value)} style={styles.scenarioSelect(theme)}>
                    {RESUME_PREVIEW_SCENARIOS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                )}
              </div>
              <div style={styles.phoneWrap}>
                <ChatPreview text={previewText} />
              </div>
            </>
          ) : (
            <div style={styles.paneEmpty(theme)}>
              <div style={styles.paneEmptyIcon(theme)}><FaCommentDots size={24} /></div>
              <div>A prévia da mensagem aparece aqui.</div>
            </div>
          )}
        </div>

        {/* Coluna 3 — edição */}
        <div style={styles.formPane(theme)}>
          {selected ? (
            <>
              <div style={styles.formScroll}>
                {(isStatusMessage(selected) || selected.isResume) && (
                  <div style={styles.editorNote(theme)}>{selType?.description}</div>
                )}

                <div>
                  <label style={styles.fieldLabel(theme)}>Nome</label>
                  <Input as="textarea" rows={2} value={selected.name} onChange={(v) => patchStep(selected.Id, { name: v })} />
                </div>

                <div style={styles.checkRow}>
                  <Checkbox checked={!!selected.firstContact} onChange={() => setFirstContact(selected.Id, !selected.firstContact)}>Usar como mensagem inicial</Checkbox>
                  <Checkbox checked={!!selected.Ativo} onChange={() => toggleActive(selected.Id, !selected.Ativo)}>Resposta ativada</Checkbox>
                </div>

                {showsKeywords(selected) && (
                  <div>
                    <label style={styles.fieldLabel(theme)}>Palavras-chave</label>
                    <p style={styles.fieldHint(theme)}>O robô responde esta mensagem quando o cliente digita uma destas palavras.</p>
                    <TagRender valuesArray={JSON.parse(selected.keywords || '[]')} onChange={(e) => changeKeywords(selected.Id, e)} />
                  </div>
                )}

                {selected.isResume ? (
                  <ResumeEditor answer={selected} onChangeAnswer={(patch) => patchStep(selected.Id, patch)} />
                ) : (
                  <>
                    <div style={{ position: 'relative' }}>
                      <label style={styles.fieldLabel(theme)}>Mensagem</label>
                      <Input id={TEXTAREA_ID} as="textarea" rows={7} value={selected.text} onChange={(v) => patchStep(selected.Id, { text: v })} />
                      <div ref={pickerBtnRef} style={{ position: 'absolute', right: 8, top: 30, zIndex: 2 }}>
                        <Whisper placement="bottom" speaker={<Tooltip>Adicionar emoji</Tooltip>}>
                          <IconButton size="sm" circle icon={<FaRegSmile />} onClick={() => setShowPicker((v) => !v)} />
                        </Whisper>
                      </div>
                      {showPicker && (
                        <div ref={pickerRef} style={{ position: 'absolute', right: 8, top: 70, zIndex: 3 }}>
                          <Picker onEmojiClick={(e) => addEmoji(selected.Id, e?.emoji)} />
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={styles.fieldLabel(theme)}>Inserir variável</label>
                      <p style={styles.fieldHint(theme)}>Variáveis são substituídas pelos valores reais ao enviar a mensagem.</p>
                      <div style={styles.chips}>
                        {GENERAL_PLACEHOLDERS.map((p) => (
                          <Whisper key={p.value} placement="top" speaker={<Tooltip>{p.tooltip}</Tooltip>}>
                            <button type="button" style={styles.chip(theme)} onClick={() => insertInTextarea(selected.Id, p.value)}>{p.label}</button>
                          </Whisper>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div style={styles.formFooter(theme)}>
                <Button appearance="subtle" onClick={() => setSelectedId(null)}>Fechar</Button>
                <Button appearance="primary" loading={saving} onClick={saveAll} style={{ backgroundColor: theme.colors.primary }}>Salvar alterações</Button>
              </div>
            </>
          ) : (
            <div style={styles.paneEmpty(theme)}>
              <div style={styles.paneEmptyIcon(theme)}><FaInbox size={24} /></div>
              <div>Selecione uma mensagem na lista para editar.</div>
            </div>
          )}
        </div>
      </div>

      {/* Configurações do robô */}
      <Drawer size="xs" placement="right" open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <Drawer.Header>
          <Drawer.Title>Configurações do robô</Drawer.Title>
          <Drawer.Actions>
            <Button onClick={() => setSettingsOpen(false)}>Cancelar</Button>
            <Button appearance="primary" loading={saving} onClick={() => { setSettingsOpen(false); saveAll(); }} style={{ backgroundColor: theme.colors.primary }}>Salvar</Button>
          </Drawer.Actions>
        </Drawer.Header>
        <Drawer.Body>
          <div style={styles.drawerField}>
            <label style={styles.fieldLabel(theme)}>Intervalo de resposta</label>
            <p style={styles.fieldHint(theme)}>Tempo mínimo entre mensagens enviadas pelo robô.</p>
            <CustomInput type="time" value={configs.interval} onChange={(v) => changeConfig('interval', v)} />
          </div>
          <div style={styles.drawerField}>
            <label style={styles.fieldLabel(theme)}>Reenviar mensagem inicial após</label>
            <p style={styles.fieldHint(theme)}>Atualmente: {milissegundosParaFormatoHora(configs.intervalMessage)}</p>
            <CustomInput type="number" min="10" max="1440" value={milissegundosParaMinutos(configs.intervalMessage)} onChange={changeResetMessage} />
          </div>
          <div style={styles.drawerField}>
            <Checkbox checked={!!configs.ignoreGroups} onChange={() => changeConfig('ignoreGroups', !configs.ignoreGroups)}>Ignorar mensagens de grupos</Checkbox>
          </div>
        </Drawer.Body>
      </Drawer>
    </div>
  );
}
